import React from 'react';
import { Box, Typography, Fade, Backdrop, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Modern pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Floating animation for particles
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
`;

// Gradient shimmer effect
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Styled backdrop with glassmorphism
const GlassmorphicBackdrop = styled(Backdrop)(({ theme }) => ({
  background: 'rgba(15, 23, 42, 0.85)',
  backdropFilter: 'blur(5px)',
  WebkitBackdropFilter: 'blur(5px)',
  zIndex: theme.zIndex.modal + 1,
}));

// Main loading container
const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '3rem 2rem',
  borderRadius: '24px',
  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.9) 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(139, 92, 246, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  minWidth: '320px',
  maxWidth: '400px',
  animation: `${pulse} 2s ease-in-out infinite`,
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 30%, rgba(139, 92, 246, 0.1) 50%, transparent 70%)',
    backgroundSize: '200% 200%',
    animation: `${shimmer} 3s ease-in-out infinite`,
    pointerEvents: 'none',
  },
}));

// Animated particles container
const ParticlesContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
});

// Individual particle
const Particle = styled(Box)(({ delay, duration }) => ({
  position: 'absolute',
  width: '4px',
  height: '4px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #a78bfa 0%, #ec4899 100%)',
  animation: `${float} ${duration || '3s'} ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
  opacity: 0.6,
}));

// Modern spinner container
const SpinnerContainer = styled(Box)({
  position: 'relative',
  marginBottom: '2rem',
});

// Custom circular progress with gradient
const GradientCircularProgress = styled(CircularProgress)({
  '& .MuiCircularProgress-circle': {
    stroke: 'url(#gradient)',
    strokeLinecap: 'round',
  },
});

// Loading text with gradient
const GradientText = styled(Typography)({
  background: 'linear-gradient(135deg, #a78bfa 0%, #ec4899 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 600,
  textAlign: 'center',
  marginTop: '1rem',
});

// Progress dots
const ProgressDots = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '1.5rem',
});

const Dot = styled(Box)(({ delay }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #a78bfa 0%, #ec4899 100%)',
  animation: `${pulse} 1.5s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const ModernLoadingOverlay = ({ 
  open = false, 
  text = 'Loading...', 
  showProgressDots = true,
  size = 60,
  thickness = 4 
}) => {
  // Generate random particles
  const particles = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
  }));

  return (
    <Fade in={open} timeout={300}>
      <GlassmorphicBackdrop open={open}>
        <LoadingContainer>
          {/* SVG gradient definition */}
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#a78bfa" />
                <stop offset="50%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
            </defs>
          </svg>

          {/* Animated particles */}
          <ParticlesContainer>
            {particles.map((particle) => (
              <Particle
                key={particle.id}
                sx={{
                  top: `${particle.top}%`,
                  left: `${particle.left}%`,
                }}
                delay={particle.delay}
                duration={particle.duration}
              />
            ))}
          </ParticlesContainer>

          {/* Main spinner */}
          <SpinnerContainer>
            <GradientCircularProgress
              size={size}
              thickness={thickness}
              sx={{
                '& .MuiCircularProgress-circle': {
                  strokeDasharray: '1, 200',
                  strokeDashoffset: 0,
                  strokeLinecap: 'round',
                  stroke: 'url(#gradient)',
                },
              }}
            />
          </SpinnerContainer>

          {/* Loading text */}
          <GradientText variant="h6" component="div">
            {text}
          </GradientText>

          {/* Progress dots */}
          {showProgressDots && (
            <ProgressDots>
              <Dot delay={0} />
              <Dot delay={0.2} />
              <Dot delay={0.4} />
            </ProgressDots>
          )}
        </LoadingContainer>
      </GlassmorphicBackdrop>
    </Fade>
  );
};

export default ModernLoadingOverlay;
